<template>
  <div class="pos-page pos-page--tablet-friendly">
    <alert-dialogue
      :show="selectedStoreId && !drawerLoading && !shiftOpen && !loading"
      type="danger"
      title="Cash Drawer Closed"
      message="No cash drawer shift is open for this store. Open a shift to continue."
      confirm-text="Open Shift"
      cancel-text="Cancel"
      icon="times"
      @confirm="
        () => {
          $router.push('/cash-drawer');
        }
      "
      @cancel="
        () => {
          $router.push('/home');
        }
      "
    />

    <div class="pos-container">
      <div class="pos-products">
        <div class="pos-shortcut-bar" aria-label="Keyboard shortcuts">
          <span
            v-for="hint in shortcutHints"
            :key="hint.key"
            class="pos-shortcut-hint"
          >
            <span class="pos-shortcut-key">{{ hint.key }}</span>
            <span>{{ hint.label }}</span>
          </span>
        </div>

        <div class="pos-search-row mb-3">
          <b-form-input
            ref="searchInput"
            v-model="searchQuery"
            class="pos-search-input form-select"
            placeholder="Search name, SKU, barcode, or loyalty phone..."
            @keyup.enter="searchProducts"
          />

          <b-button
            variant="outline-secondary"
            class="pos-search-misc-btn"
            @click="openMiscModal"
          >
            Misc item
          </b-button>
        </div>

        <p class="mb-0 pb-1">Filter</p>
        <div class="pos-search-row">
          <b-col md="9" class="mb-3">
            <div class="stock-filter">
              <span class="filter-label">Stock:</span>

              <b-form-radio-group
                v-model="selectedStockStatus"
                :options="stockStatusOptions"
                name="stock-status"
                buttons
                button-variant="outline-secondary"
                class="stock-radio-group"
                @change="filterProducts"
              />
            </div>
          </b-col>
          <b-col md="3">
            <div class="select-wrapper">
              <b-form-select
                v-model="selectedCategory"
                :options="categoryOptions"
                class="form-select"
                @change="filterProducts()"
              />
            </div>
          </b-col>
        </div>

        <quick-key-grid
          :products="visibleQuickKeys"
          :source="quickKeySource"
          @add="addToCart"
        />

        <div v-if="loading" class="text-center py-4">
          <b-spinner />
        </div>
        <div v-else-if="!visibleProducts.length" class="pos-empty-catalog">
          {{
            searchNeedle
              ? `No items match “${searchQuery.trim()}”`
              : "No sellable items for this store."
          }}
        </div>
        <div v-else class="product-grid">
          <product-card
            v-for="product in visibleProducts"
            :key="product.id"
            :product="product"
            :add-to-cart="addToCart"
          />
        </div>
      </div>

      <div class="pos-cart">
        <div class="d-flex justify-content-between align-items-center mb-3">
          <h5 class="mb-0">Cart</h5>
          <b-button
            variant="outline-secondary"
            class="pos-held-btn"
            @click="openHeldPanel"
          >
            Held
            <b-badge
              v-if="heldSales.length"
              pill
              :variant="heldByOthersCount ? 'warning' : 'secondary'"
              class="ml-1"
            >
              {{ heldSales.length }}
            </b-badge>
          </b-button>
        </div>

        <div v-if="activeSaleId" class="held-banner mb-2">
          <span class="small">
            Recalled sale
            <strong>{{ recalledSale?.saleNumber || "" }}</strong>
          </span>
        </div>

        <div class="cart-items flex-grow-1">
          <div v-if="!cartItems.length" class="text-muted small">
            No items yet
          </div>
          <div
            v-for="item in cartItems"
            :key="item.cartLineId"
            class="cart-item"
          >
            <div class="flex-grow-1">
              <strong>{{ item.miscLabel || item.productName }}</strong>
              <p class="text-muted small mb-1">
                {{ formatMoney(item.unitPrice) }} each
              </p>
              <div v-if="!item.readOnly" class="qty-controls">
                <icon-button
                  :func="() => changeQty(item.cartLineId, -1)"
                  :icon="'minus'"
                  :title="'Decrease quantity'"
                  :classes="'akkurate-danger-btn pos-qty-btn'"
                  :btnName="''"
                />
                <span class="qty-value">{{ item.quantity }}</span>
                <icon-button
                  :func="() => changeQty(item.cartLineId, 1)"
                  :icon="'plus'"
                  :title="'Increase quantity'"
                  :classes="'akkurate-green-btn pos-qty-btn'"
                  :btnName="''"
                />
              </div>
              <div v-else class="small text-muted">
                Qty: {{ item.quantity }}
              </div>
            </div>
            <div class="text-end">
              <div class="fw-semibold">
                {{ formatMoney(item.quantity * item.unitPrice) }}
              </div>
              <icon-button
                v-if="!item.readOnly"
                :func="() => removeFromCart(item.cartLineId)"
                :icon="'times'"
                :title="'Remove Item'"
                :classes="'akkurate-close-btn'"
                :btnName="''"
              />
            </div>
          </div>
        </div>

        <div class="total-section">
          <div class="d-flex justify-content-between small text-muted">
            <span>Subtotal</span>
            <span>{{ formatMoney(cartSubtotal) }}</span>
          </div>
          <div
            v-if="cartTax > 0"
            class="d-flex justify-content-between small text-muted mt-1"
          >
            <span>Tax / VAT</span>
            <span>{{ formatMoney(cartTax) }}</span>
          </div>
          <div
            v-if="previewDiscount > 0"
            class="d-flex justify-content-between small text-success mt-1"
          >
            <span>
              Discount
              <span v-if="preview?.promotionLabel" class="text-muted">
                ({{ preview.promotionLabel }})
              </span>
            </span>
            <span>-{{ formatMoney(previewDiscount) }}</span>
          </div>
          <div class="total-amount mt-2">
            Total: {{ formatMoney(cartTotal) }}
            <b-spinner v-if="previewLoading" small class="ml-1" />
          </div>
          <div
            class="pos-cart-actions"
            :style="{
              display: 'flex',
              justifyContent: 'space-between',
              gap: '5px',
            }"
          >
            <b-button
              class="pos-action-btn akkurate-green-btn"
              :disabled="
                !cartItems.length || processing || drawerLoading || !shiftOpen
              "
              @click="openPayment"
            >
              Pay
            </b-button>
            <b-button
              class="pos-action-btn"
              variant="outline-warning"
              :disabled="!cartItems.length || processing || !!activeSaleId"
              @click="holdSale"
            >
              Hold
            </b-button>
            <b-button
              class="pos-action-btn"
              variant="outline-secondary"
              :disabled="!cartItems.length || processing"
              @click="clearCart"
            >
              Clear
            </b-button>
          </div>
        </div>
      </div>
    </div>

    <!-- Payment modal -->
    <b-modal
      v-model="showPayment"
      title="Payment"
      size="lg"
      @shown="onPaymentShown"
      @hidden="resetPayment"
    >
      <b-form-group label="Customer (optional)">
        <div v-if="selectedCustomer" class="customer-chip mb-2">
          <div>
            <strong>{{ selectedCustomer.fullName }}</strong>
            <span class="text-muted small d-block">
              {{ selectedCustomer.loyaltyPoints }} pts
              <span v-if="selectedCustomer.discountPercent > 0">
                · {{ selectedCustomer.discountPercent }}% member discount
              </span>
            </span>
          </div>
          <icon-button
            :func="() => clearCustomer()"
            :icon="'times'"
            :title="'Remove Customer'"
            :classes="'akkurate-close-btn'"
            :btnName="''"
          />
        </div>
        <div v-else-if="!activeSaleId">
          <b-input-group>
            <b-form-input
              class="form-control"
              v-model="customerSearch"
              placeholder="Search name, phone, or email..."
              @input="debouncedCustomerSearch"
            />
            <b-input-group-append>
              <icon-button
                :func="() => (showQuickCustomer = true)"
                :icon="'plus'"
                :title="'Add new customer'"
                :classes="'akkurate-green-btn btn-size2'"
                :btnName="'New'"
              />
            </b-input-group-append>
          </b-input-group>
          <div v-if="customerResults.length" class="customer-results mt-2">
            <button
              v-for="c in customerResults"
              :key="c.id"
              type="button"
              class="customer-result-item"
              @click="selectCustomer(c)"
            >
              <span>{{ c.fullName }}</span>
              <span class="text-muted small"
                >{{ c.phone || c.email }} · {{ c.loyaltyPoints }} pts</span
              >
            </button>
          </div>
        </div>
      </b-form-group>

      <b-form-group
        v-if="
          selectedCustomer &&
          selectedCustomer.loyaltyPoints > 0 &&
          !activeSaleId
        "
        label="Redeem loyalty points"
      >
        <b-form-input
          class="form-control"
          v-model.number="loyaltyPointsToRedeem"
          type="number"
          min="0"
          :max="maxRedeemablePoints"
          step="1"
          @change="refreshPreview"
        />
        <b-form-text>
          Max {{ maxRedeemablePoints }} pts ({{
            formatMoney(loyaltyPointsToRedeem * pointValue)
          }}
          off)
        </b-form-text>
      </b-form-group>

      <b-form-group v-if="!activeSaleId" label="Promotion code">
        <b-input-group class="mb-3">
          <b-form-input
            class="form-control"
            v-model="promotionCode"
            placeholder="Enter code"
            @keyup.enter="validatePromotion"
          />
          <b-input-group-append>
            <icon-button
              :func="() => validatePromotion()"
              :icon="'plus'"
              :title="'Apply'"
              :classes="'akkurate-green-btn btn-size2'"
              :btnName="'Apply'"
              :disabled="!promotionCode.trim() || validatingPromotion"
            />
          </b-input-group-append>
        </b-input-group>
        <b-form-text v-if="promotionInfo" class="text-success mb-2">
          {{ promotionInfo.name }} applied
        </b-form-text>
        <b-form-text v-if="promotionError" class="text-danger">
          {{ promotionError }}
        </b-form-text>
      </b-form-group>

      <div class="payment-summary mb-3">
        <div class="d-flex justify-content-between small">
          <span>Subtotal</span><span>{{ formatMoney(cartSubtotal) }}</span>
        </div>
        <div
          v-if="cartTax > 0"
          class="d-flex justify-content-between small mt-1"
        >
          <span>Tax / VAT</span><span>{{ formatMoney(cartTax) }}</span>
        </div>
        <div
          v-if="previewDiscount > 0"
          class="d-flex justify-content-between small mt-1 text-success"
        >
          <span>Discount</span><span>-{{ formatMoney(previewDiscount) }}</span>
        </div>
        <div class="d-flex justify-content-between fw-bold mt-2">
          <span>Amount due</span><span>{{ formatMoney(amountDue) }}</span>
        </div>
        <div
          v-if="paymentLines.length"
          class="d-flex justify-content-between small mt-1 text-muted"
        >
          <span>Paid so far</span><span>{{ formatMoney(amountPaid) }}</span>
        </div>
        <div
          v-if="remainingDue > 0 && paymentLines.length"
          class="d-flex justify-content-between small mt-1"
        >
          <span>Remaining</span
          ><span class="fw-semibold">{{ formatMoney(remainingDue) }}</span>
        </div>
      </div>

      <div v-if="paymentLines.length" class="split-payments mb-3">
        <h6 class="small text-uppercase text-muted mb-2">Payments added</h6>
        <div
          v-for="(line, idx) in paymentLines"
          :key="idx"
          class="split-payment-line"
        >
          <span>{{ line.method }}</span>
          <span>{{ formatMoney(line.amount) }}</span>
          <icon-button
            :func="() => removePaymentLine(idx)"
            :icon="'times'"
            :title="'Remove'"
            :classes="'akkurate-close-btn'"
            :btnName="''"
          />
        </div>
      </div>

      <b-form-group label="Payment method" class="mb-2">
        <b-form-select
          v-model="currentPayment.method"
          class="akkurate_input filter-list filter-list-choice status-filter custom-select form-select"
          :options="paymentMethods"
        />
      </b-form-group>

      <b-form-group label="Amount">
        <b-form-input
          class="form-control"
          v-model.number="currentPayment.amount"
          type="number"
          min="0"
          step="0.01"
        />
      </b-form-group>

      <b-form-group
        v-if="currentPayment.method === 'Cash'"
        label="Amount tendered"
      >
        <div class="tender-chips mb-2">
          <button
            v-for="opt in tenderOptions"
            :key="opt.label"
            type="button"
            class="tender-chip"
            @click="applyTender(opt.value)"
          >
            {{ opt.label }}
          </button>
        </div>
        <b-form-input
          class="form-control"
          v-model.number="currentPayment.tendered"
          type="number"
          min="0"
          step="0.01"
        />
      </b-form-group>

      <b-form-group
        v-if="currentPayment.method !== 'Cash'"
        label="Reference (optional)"
      >
        <b-form-input
          class="form-control"
          v-model="currentPayment.reference"
          placeholder="Transaction reference"
        />
      </b-form-group>

      <div
        v-if="currentPayment.method === 'MobileMoney'"
        class="mobile-money-section mb-3"
      >
        <b-form-group label="Mobile money phone">
          <b-form-input
            class="form-control"
            v-model="mobileMoneyPhone"
            placeholder="e.g. 0244123456"
          />
        </b-form-group>
        <b-button
          variant="outline-primary"
          class="btn-size btn-radius"
          :disabled="!mobileMoneyPhone.trim() || stkLoading"
          @click="initiateStk"
        >
          <b-spinner v-if="stkLoading" small class="mr-1" />
          Initiate STK
        </b-button>
      </div>

      <div v-if="currentPayment.method === 'Cash'" class="payment-summary">
        <div class="d-flex justify-content-between">
          <span>Change (this line)</span>
          <strong :class="{ 'text-danger': currentChangeDue < 0 }">{{
            formatMoney(currentChangeDue)
          }}</strong>
        </div>
      </div>

      <div class="mt-3">
        <icon-button
          :func="() => addPaymentLine()"
          :icon="'check'"
          :title="'Complete sale'"
          :classes="'akkurate-green-btn btn-size2'"
          :btnName="'Add payment line'"
          :disabled="!canAddPaymentLine"
        />
      </div>

      <template #modal-footer>
        <icon-button
          :func="() => (showPayment = false)"
          :icon="'times'"
          :title="'Cancel'"
          :classes="'akkurate-danger-bg btn-size2'"
          :btnName="'Cancel'"
        />
        <icon-button
          :func="() => completeSale()"
          :icon="'check'"
          :title="'Complete sale'"
          :classes="'akkurate-green-btn btn-size2'"
          :btnName="'Complete sale'"
          :disabled="!canCompletePayment || processing"
        />
      </template>
    </b-modal>

    <hold-sale-modal
      v-model="showHoldModal"
      :customer-name="selectedCustomer?.fullName || ''"
      :customer-phone="selectedCustomer?.phone || ''"
      :item-count="cartItems.length"
      :first-item-name="holdFirstItemName"
      @confirm="confirmHold"
    />

    <held-sales-panel
      v-model="showHeldPanel"
      :held-sales="heldSales"
      :current-user-id="currentUserId"
      :can-audit-others="canAuditFloor"
      @recall="recallHeldSale"
      @print="printHeldReceipt"
    />

    <b-modal
      v-model="showMiscModal"
      title="Miscellaneous item"
      @hidden="onMiscModalHidden"
    >
      <b-form-group label="Description">
        <b-form-input
          class="form-control"
          v-model="miscForm.label"
          placeholder="Item description"
        />
      </b-form-group>
      <b-form-group label="Price">
        <b-form-input
          class="form-control"
          v-model.number="miscForm.unitPrice"
          type="number"
          min="0.01"
          step="0.01"
        />
      </b-form-group>
      <template #modal-footer>
        <icon-button
          :func="() => (showMiscModal = false)"
          :icon="'times'"
          :title="'Cancel'"
          :classes="'akkurate-danger-bg btn-size2'"
          :btnName="'Cancel'"
        />
        <icon-button
          :func="() => addMiscItem()"
          :icon="'plus'"
          :title="'Add to cart'"
          :classes="'akkurate-green-btn btn-size2'"
          :btnName="'Add to cart'"
          :disabled="!miscForm.unitPrice || miscForm.unitPrice <= 0"
        />
      </template>
    </b-modal>

    <b-modal
      v-model="showQuickCustomer"
      title="Quick add customer"
      @ok="createCustomer"
    >
      <b-form-group label="First name">
        <b-form-input class="form-control" v-model="quickCustomer.firstName" />
      </b-form-group>
      <b-form-group label="Last name">
        <b-form-input class="form-control" v-model="quickCustomer.lastName" />
      </b-form-group>
      <b-form-group label="Phone">
        <b-form-input class="form-control" v-model="quickCustomer.phone" />
      </b-form-group>
      <b-form-group label="Email">
        <b-form-input
          class="form-control"
          v-model="quickCustomer.email"
          type="email"
        />
      </b-form-group>
    </b-modal>

    <!-- Receipt modal -->
    <b-modal
      v-model="showReceipt"
      :title="receiptModalTitle"
      size="lg"
      ok-only
      ok-title="Done"
      @hidden="onReceiptHidden"
    >
      <div v-if="receiptHtml" class="receipt-preview" v-html="receiptHtml" />
      <template #modal-footer="{ ok }">
        <icon-button
          :func="() => printReceipt()"
          :icon="'print'"
          :title="'Print receipt'"
          :classes="'akkurate-green-btn btn-size2'"
          :btnName="'Print'"
        />
        <icon-button
          :func="() => downloadEscPos()"
          :icon="'download'"
          :title="'Download ESC/POS'"
          :classes="'akkurate-gray-btn btn-size2'"
          :btnName="'ESC/POS'"
        />
        <icon-button
          :func="() => ok()"
          :icon="'check'"
          :title="'Done'"
          :classes="'akkurate-gray-btn btn-size2'"
          :btnName="'Done'"
        />
      </template>
    </b-modal>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import ApiService from "@/core/services/api.service";
import IconButton from "@/components/IconButton.vue";
import AlertDialogue from "@/components/AlertDialogue.vue";
import ProductCard from "../../../../components/ProductCard.vue";
import QuickKeyGrid from "@/components/pos/QuickKeyGrid.vue";
import HeldSalesPanel from "@/components/pos/HeldSalesPanel.vue";
import HoldSaleModal from "@/components/pos/HoldSaleModal.vue";
import { focusScanner, shouldRefocusAfterModal } from "@/core/helpers/posFocus";
import {
  createPosKeyboardHandler,
  POS_SHORTCUT_HINTS,
} from "@/core/helpers/posKeyboard";
import { buildTenderOptions } from "@/core/helpers/posTender";
import {
  previewSale,
  clearPreviewTimer,
} from "@/core/services/posPreview.service";
import {
  enqueueOfflineSale,
  flushOfflineQueue,
  isOnline,
} from "@/core/services/offlineQueue.service";
import { printEscPos } from "@/core/services/escpos.service";

export default {
  name: "POS",
  components: {
    IconButton,
    AlertDialogue,
    ProductCard,
    QuickKeyGrid,
    HeldSalesPanel,
    HoldSaleModal,
  },
  data() {
    return {
      products: [],
      cartItems: [],
      cartLineSeq: 0,
      searchQuery: "",
      selectedCategory: "",
      selectedStockStatus: "",

      categories: [],
      searchResults: null,
      searchSeq: 0,
      loading: false,
      processing: false,
      searchTimer: null,
      showPayment: false,
      showQuickCustomer: false,
      showReceipt: false,
      showHoldModal: false,
      showHeldPanel: false,
      showMiscModal: false,
      receiptHtml: "",
      receiptStatus: "",
      drawerSession: null,
      drawerLoading: false,
      lastSaleId: null,
      selectedCustomer: null,
      customerSearch: "",
      customerResults: [],
      customerSearchTimer: null,
      loyaltyPointsToRedeem: 0,
      pointValue: 0.01,
      quickCustomer: {
        firstName: "",
        lastName: "",
        phone: "",
        email: "",
      },
      preview: null,
      previewLoading: false,
      quickKeyProducts: [],
      quickKeySource: "",
      tenderPresets: [5, 10, 20, 50, 100],
      heldSales: [],
      activeSaleId: null,
      recalledSale: null,
      promotionCode: "",
      promotionInfo: null,
      promotionError: "",
      validatingPromotion: false,
      paymentLines: [],
      currentPayment: {
        method: "Cash",
        amount: 0,
        tendered: 0,
        reference: "",
      },
      mobileMoneyPhone: "",
      stkLoading: false,
      miscProduct: null,
      miscForm: { label: "", unitPrice: 0 },
      keyboardHandler: null,
      shortcutHints: POS_SHORTCUT_HINTS,
      paymentMethods: [
        { value: "Cash", text: "Cash" },
        { value: "Card", text: "Card" },
        { value: "MobileMoney", text: "Mobile Money" },
      ],
    };
  },
  computed: {
    ...mapGetters(["selectedStoreId", "currentUser", "hasPermission"]),
    currentUserId() {
      return this.currentUser?.id || "";
    },
    canAuditFloor() {
      return this.hasPermission("SALES_AUDIT");
    },
    heldByOthersCount() {
      if (!this.canAuditFloor) return 0;
      const mine = this.currentUserId;
      return this.heldSales.filter((s) => s.userId && s.userId !== mine).length;
    },
    searchNeedle() {
      return this.searchQuery.trim().toLowerCase();
    },
    visibleProducts() {
      if (this.searchResults) return this.searchResults;
      return this.filterCatalog(this.products);
    },
    visibleQuickKeys() {
      return this.filterCatalog(this.quickKeyProducts);
    },
    shiftOpen() {
      return !!this.drawerSession;
    },
    holdFirstItemName() {
      const first = this.cartItems[0];
      if (!first) return "";
      return first.miscLabel || first.productName || "";
    },
    receiptModalTitle() {
      if (this.receiptStatus === "held") return "Held receipt (not paid)";
      if (this.receiptStatus === "pending") return "Pending receipt (not paid)";
      return "Receipt";
    },
    localSubtotal() {
      return this.cartItems.reduce(
        (sum, i) => sum + i.quantity * i.unitPrice,
        0,
      );
    },
    categoryOptions() {
      return [
        {
          value: "",
          text: "All Categories",
        },
        ...this.categories.map((category) => ({
          value: category,
          text: category,
        })),
      ];
    },

    stockStatusOptions() {
      return [
        {
          value: "",
          text: "All Stock",
        },
        {
          value: "in_stock",
          text: "In Stock",
        },
        {
          value: "low_stock",
          text: "Low Stock",
        },
        {
          value: "out_of_stock",
          text: "Out of Stock",
        },
      ];
    },
    cartSubtotal() {
      if (this.activeSaleId && this.recalledSale) {
        return this.recalledSale.subtotal ?? this.localSubtotal;
      }
      return this.preview?.subtotal ?? this.localSubtotal;
    },
    cartTax() {
      if (this.activeSaleId && this.recalledSale) {
        return this.recalledSale.tax ?? 0;
      }
      return this.preview?.tax ?? 0;
    },
    previewDiscount() {
      if (this.activeSaleId && this.recalledSale) {
        return this.recalledSale.discount ?? 0;
      }
      return this.preview?.discount ?? 0;
    },
    cartTotal() {
      if (this.activeSaleId && this.recalledSale) {
        return this.recalledSale.total ?? this.localSubtotal;
      }
      return this.preview?.total ?? this.localSubtotal;
    },
    amountDue() {
      if (this.activeSaleId && this.recalledSale) {
        const paid = (this.recalledSale.payments || []).reduce(
          (sum, p) => sum + Number(p.amount || 0),
          0,
        );
        return Math.max(0, this.recalledSale.total - paid);
      }
      return this.cartTotal;
    },
    maxRedeemablePoints() {
      if (!this.selectedCustomer) return 0;
      const grossAfterMember = Math.max(0, this.cartTotal);
      const maxByTotal = Math.floor(grossAfterMember / this.pointValue);
      return Math.min(this.selectedCustomer.loyaltyPoints || 0, maxByTotal);
    },
    tenderOptions() {
      return buildTenderOptions(
        this.remainingDue || this.amountDue,
        this.tenderPresets,
      );
    },
    amountPaid() {
      return this.paymentLines.reduce(
        (sum, p) => sum + Number(p.amount || 0),
        0,
      );
    },
    remainingDue() {
      const baseDue =
        this.activeSaleId && this.recalledSale
          ? Math.max(
              0,
              this.recalledSale.total -
                (this.recalledSale.payments || []).reduce(
                  (s, p) => s + Number(p.amount || 0),
                  0,
                ),
            )
          : this.cartTotal;
      return Math.max(0, baseDue - this.amountPaid);
    },
    currentChangeDue() {
      if (this.currentPayment.method !== "Cash") return 0;
      return (
        Number(this.currentPayment.tendered || 0) -
        Number(this.currentPayment.amount || 0)
      );
    },
    canAddPaymentLine() {
      const amt = Number(this.currentPayment.amount || 0);
      if (amt <= 0) return false;
      if (this.currentPayment.method === "Cash") {
        return Number(this.currentPayment.tendered || 0) >= amt;
      }
      return true;
    },
    canCompletePayment() {
      if (!this.shiftOpen) return false;
      const payments = this.getPaymentsToSubmit();
      if (!payments.length) return false;
      const total = payments.reduce((s, p) => s + Number(p.amount || 0), 0);
      if (total < this.amountDue - 0.009) return false;
      return payments.every(
        (p) =>
          p.method !== "Cash" ||
          Number(p.tendered || p.amount) >= Number(p.amount),
      );
    },
  },
  watch: {
    searchQuery() {
      this.searchResults = null;
      this.searchSeq += 1;
      this.debouncedSearch();
    },
    selectedStoreId() {
      this.clearCart();
      this.loadProducts();
      this.loadCategories();
      this.loadDrawerSession();
      this.loadQuickKeys();
      this.loadHeldSales();
    },
    cartItems: {
      handler() {
        if (!this.activeSaleId) this.refreshPreview();
      },
      deep: true,
    },
    loyaltyPointsToRedeem() {
      if (this.loyaltyPointsToRedeem > this.maxRedeemablePoints) {
        this.loyaltyPointsToRedeem = this.maxRedeemablePoints;
      }
    },
    remainingDue(val) {
      if (this.showPayment && val > 0) {
        this.currentPayment.amount = val;
        if (this.currentPayment.method === "Cash") {
          this.currentPayment.tendered = val;
        }
      }
    },
    "$route.query.recall": {
      immediate: true,
      handler(id) {
        if (id) this.recallFromQuery(id);
      },
    },
  },
  mounted() {
    this.keyboardHandler = createPosKeyboardHandler(this);
    document.addEventListener("keydown", this.keyboardHandler);
    this.loadProducts();
    this.loadCategories();
    this.loadDrawerSession();

    this.$nextTick(() => this.$refs.searchInput?.focus());
    this.loadQuickKeys();
    this.loadHeldSales();
    this.loadSettings();
    this.flushOfflineIfOnline();
    focusScanner(this);
  },
  beforeDestroy() {
    document.removeEventListener("keydown", this.keyboardHandler);
    clearTimeout(this.searchTimer);
    clearPreviewTimer();
  },
  methods: {
    filterProducts() {
      this.loadProducts();
    },
    selectCategory(category) {
      this.selectedCategory = category;
      this.filterProducts();
    },
    async loadCategories() {
      try {
        this.categories = await ApiService.get("products/categories");
      } catch (e) {
        console.error("Failed to load product categories:", e);

        this.categories = [];
      }
    },
    focusSearch() {
      this.$refs.searchInput?.focus();
      this.$refs.searchInput?.select?.();
    },
    closeModals() {
      this.showPayment = false;
      this.showReceipt = false;
      this.showQuickCustomer = false;
      this.showHoldModal = false;
      this.showHeldPanel = false;
      this.showMiscModal = false;
    },
    async loadSettings() {
      try {
        const settings = await ApiService.get("settings");
        if (settings?.tenderPresets?.length) {
          this.tenderPresets = settings.tenderPresets;
        }
      } catch (e) {
        /* keep defaults */
      }
    },
    async loadDrawerSession() {
      if (!this.selectedStoreId) {
        this.drawerSession = null;
        return;
      }
      this.drawerLoading = true;
      try {
        const response = await ApiService.get("cash-drawer/current", {
          storeId: this.selectedStoreId,
        });
        this.drawerSession = response.currentSession;
      } catch (e) {
        this.drawerSession = null;
      } finally {
        this.drawerLoading = false;
      }
    },
    async loadQuickKeys() {
      if (!this.selectedStoreId) {
        this.quickKeyProducts = [];
        return;
      }
      try {
        const data = await ApiService.get("pos/quick-keys", {
          storeId: this.selectedStoreId,
        });
        this.quickKeyProducts = data.products || [];
        this.quickKeySource = data.source || "";
      } catch (e) {
        this.quickKeyProducts = [];
        this.quickKeySource = "";
      }
    },
    async loadHeldSales() {
      if (!this.selectedStoreId) {
        this.heldSales = [];
        return;
      }
      try {
        this.heldSales = await ApiService.get("sales/held/list", {
          storeId: this.selectedStoreId,
          limit: 50,
        });
      } catch (e) {
        this.heldSales = [];
      }
    },
    stockLabel(product) {
      const available = product.stock?.available ?? 0;
      return available > 0 ? `${available} in stock` : "Out of stock";
    },
    stockClass(product) {
      const available = product.stock?.available ?? 0;
      if (available <= 0) return "text-danger";
      if (available <= 5) return "text-warning";
      return "text-muted";
    },
    isOutOfStock(product) {
      return (product.stock?.available ?? 0) <= 0;
    },
    async loadProducts() {
      if (!this.selectedStoreId) return;

      this.loading = true;
      this.searchResults = null;
      try {
        this.products = await ApiService.get("products", {
          storeId: this.selectedStoreId,
          sellableOnly: true,
          category: this.selectedCategory || undefined,
          stockStatus: this.selectedStockStatus || undefined,
        });
      } finally {
        this.loading = false;
      }
    },
    productMatchesQuery(product, needle) {
      return [product.name, product.sku, product.barcode, product.category]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(needle));
    },
    filterCatalog(list) {
      const needle = this.searchNeedle;
      if (!needle || !Array.isArray(list)) return list || [];
      return list.filter((product) =>
        this.productMatchesQuery(product, needle),
      );
    },
    debouncedSearch() {
      clearTimeout(this.searchTimer);
      const q = this.searchQuery.trim();
      if (!q) {
        this.searchResults = null;
        return;
      }
      this.searchTimer = setTimeout(this.searchProducts, 300);
    },
    isPhoneQuery(q) {
      return /^\d{10,}$/.test(q);
    },
    async searchProducts() {
      const q = this.searchQuery.trim();
      if (!q) {
        this.searchResults = null;
        return;
      }

      if (/^\d{8,}$/.test(q)) {
        try {
          this.products = await ApiService.get("products", {
            query: q,
            storeId: this.selectedStoreId,
            sellableOnly: true,
            category: this.selectedCategory || undefined,
            stockStatus: this.selectedStockStatus || undefined,
          });
          if (this.searchQuery.trim() !== q) return;
          this.addToCart(product);
          this.searchQuery = "";
          return;
        } catch (e) {
          if (!this.isPhoneQuery(q)) {
            /* fall through to text / local search */
          }
        }
      }

      if (this.isPhoneQuery(q)) {
        try {
          const customers = await ApiService.get("customers", {
            query: q,
            limit: 1,
          });
          if (this.searchQuery.trim() !== q) return;
          if (customers.length) {
            this.selectCustomer(customers[0]);
            this.searchQuery = "";
            focusScanner(this);
            return;
          }
        } catch (e) {
          /* fall through */
        }
      }

      if (this.filterCatalog(this.products).length) {
        return;
      }

      const seq = ++this.searchSeq;
      try {
        const results = await ApiService.get("products", {
          query: q,
          storeId: this.selectedStoreId,
          sellableOnly: true,
        });
        if (seq !== this.searchSeq || this.searchQuery.trim() !== q) return;
        this.searchResults = results;
      } catch (e) {
        if (seq !== this.searchSeq) return;
        this.searchResults = [];
      }
    },
    nextCartLineId(prefix = "line") {
      this.cartLineSeq += 1;
      return `${prefix}-${this.cartLineSeq}`;
    },
    addToCart(product) {
      const available = product.stock?.available ?? 0;
      if (available <= 0) {
        toast.fire({
          title: `${product.name} is out of stock`,
          icon: "warning",
        });
        return;
      }
      const existing = this.cartItems.find(
        (i) => i.productId === product.id && !i.miscLabel && !i.readOnly,
      );
      if (existing) {
        if (existing.quantity >= existing.maxAvailable) {
          toast.fire({
            title: `Only ${existing.maxAvailable} available`,
            icon: "warning",
          });
          return;
        }
        existing.quantity += 1;
      } else {
        this.cartItems.push({
          cartLineId: this.nextCartLineId("prod"),
          productId: product.id,
          productName: product.name,
          unitPrice: Number(product.unitPrice),
          taxRate: Number(product.taxRate || 0),
          quantity: 1,
          maxAvailable: available,
        });
      }
      focusScanner(this);
    },
    changeQty(cartLineId, delta) {
      const item = this.cartItems.find((i) => i.cartLineId === cartLineId);
      if (!item || item.readOnly) return;
      const next = item.quantity + delta;
      if (next <= 0) {
        this.removeFromCart(cartLineId);
        return;
      }
      if (next > item.maxAvailable) {
        toast.fire({
          title: `Only ${item.maxAvailable} available`,
          icon: "warning",
        });
        return;
      }
      item.quantity = next;
    },
    removeFromCart(cartLineId) {
      this.cartItems = this.cartItems.filter(
        (i) => i.cartLineId !== cartLineId,
      );
    },
    clearCart() {
      this.cartItems = [];
      this.preview = null;
      this.activeSaleId = null;
      this.recalledSale = null;
      this.selectedCustomer = null;
      this.loyaltyPointsToRedeem = 0;
      this.promotionCode = "";
      this.promotionInfo = null;
      this.promotionError = "";
      clearPreviewTimer();
      focusScanner(this);
    },
    buildPreviewPayload() {
      return {
        storeId: this.selectedStoreId,
        customerId: this.selectedCustomer?.id || null,
        loyaltyPointsRedeemed: Number(this.loyaltyPointsToRedeem) || 0,
        promotionCode: this.promotionCode.trim() || null,
        items: this.cartItems.map((i) => ({
          productId: i.productId,
          quantity: i.quantity,
          unitPrice: i.unitPrice,
          discount: 0,
        })),
      };
    },
    async refreshPreview() {
      if (!this.cartItems.length || this.activeSaleId) {
        this.preview = null;
        return;
      }
      this.previewLoading = true;
      try {
        this.preview = await previewSale(this.buildPreviewPayload());
      } catch (e) {
        /* keep previous preview */
      } finally {
        this.previewLoading = false;
      }
    },
    buildSalePayload() {
      return {
        storeId: this.selectedStoreId,
        customerId: this.selectedCustomer?.id || null,
        loyaltyPointsRedeemed: Number(this.loyaltyPointsToRedeem) || 0,
        promotionCode: this.promotionCode.trim() || null,
        items: this.cartItems.map((i) => ({
          productId: i.productId,
          quantity: i.quantity,
          unitPrice: i.unitPrice,
          miscLabel: i.miscLabel || null,
          discount: 0,
        })),
        discount: 0,
      };
    },
    async openPayment() {
      if (!this.selectedStoreId) {
        toast.fire({
          title: "No store assigned to your account.",
          icon: "danger",
        });
        return;
      }
      await this.loadDrawerSession();
      if (!this.shiftOpen) {
        toast.fire({
          title: "Open a cash drawer shift before completing sales.",
          icon: "warning",
        });
        return;
      }
      this.showPayment = true;
    },
    onPaymentShown() {
      this.initCurrentPayment();
    },
    initCurrentPayment() {
      const due = this.remainingDue || this.amountDue;
      this.currentPayment = {
        method: "Cash",
        amount: due,
        tendered: due,
        reference: "",
      };
    },
    resetPayment() {
      this.paymentLines = [];
      this.currentPayment = {
        method: "Cash",
        amount: 0,
        tendered: 0,
        reference: "",
      };
      this.mobileMoneyPhone = "";
      this.promotionCode = "";
      this.promotionInfo = null;
      this.promotionError = "";
      if (this.activeSaleId) {
        this.activeSaleId = null;
        this.recalledSale = null;
        this.cartItems = [];
      } else {
        this.customerSearch = "";
        this.customerResults = [];
        this.loyaltyPointsToRedeem = 0;
      }
      if (shouldRefocusAfterModal("payment", this)) {
        focusScanner(this);
      }
    },
    onReceiptHidden() {
      if (shouldRefocusAfterModal("receipt", this)) {
        focusScanner(this);
      }
    },
    onMiscModalHidden() {
      if (shouldRefocusAfterModal("misc", this)) {
        focusScanner(this);
      }
    },
    applyTender(value) {
      this.currentPayment.tendered = value;
      if (!this.currentPayment.amount || this.currentPayment.amount <= 0) {
        this.currentPayment.amount = this.remainingDue || this.amountDue;
      }
    },
    addPaymentLine() {
      if (!this.canAddPaymentLine) return;
      this.paymentLines.push({
        method: this.currentPayment.method,
        amount: Number(this.currentPayment.amount),
        tendered:
          this.currentPayment.method === "Cash"
            ? Number(this.currentPayment.tendered || this.currentPayment.amount)
            : null,
        reference: this.currentPayment.reference || "",
      });
      const remaining = this.remainingDue;
      this.currentPayment = {
        method: "Cash",
        amount: remaining,
        tendered: remaining,
        reference: "",
      };
    },
    removePaymentLine(idx) {
      this.paymentLines.splice(idx, 1);
      this.initCurrentPayment();
    },
    getPaymentsToSubmit() {
      const lines = [...this.paymentLines];
      const pending = Number(this.currentPayment.amount || 0);
      if (pending > 0 && this.remainingDue > 0) {
        const alreadyInLines = lines.some(
          (l) =>
            l.method === this.currentPayment.method &&
            l.amount === pending &&
            l.reference === (this.currentPayment.reference || ""),
        );
        if (!alreadyInLines) {
          lines.push({
            method: this.currentPayment.method,
            amount: pending,
            tendered:
              this.currentPayment.method === "Cash"
                ? Number(
                    this.currentPayment.tendered || this.currentPayment.amount,
                  )
                : null,
            reference: this.currentPayment.reference || "",
          });
        }
      }
      return lines;
    },
    holdSale() {
      if (!this.cartItems.length || this.activeSaleId) return;
      this.showHoldModal = true;
    },
    async confirmHold(label) {
      if (!this.cartItems.length) return;
      this.processing = true;
      try {
        await ApiService.post("sales/hold", {
          storeId: this.selectedStoreId,
          holdLabel: label || null,
          customerId: this.selectedCustomer?.id || null,
          promotionCode: this.promotionCode.trim() || null,
          items: this.cartItems.map((i) => ({
            productId: i.productId,
            quantity: i.quantity,
            unitPrice: i.unitPrice,
            miscLabel: i.miscLabel || null,
          })),
        });
        this.clearCart();
        await this.loadHeldSales();
        toast.fire({ title: "Sale held", icon: "success" });
        focusScanner(this);
      } catch (e) {
        toast.fire({
          title: e?.response?.data?.message || "Could not hold sale",
          icon: "danger",
        });
      } finally {
        this.processing = false;
      }
    },
    openHeldPanel() {
      this.loadHeldSales();
      this.showHeldPanel = true;
    },
    async recallFromQuery(saleId) {
      if (!saleId || this.activeSaleId === saleId) return;
      await this.recallHeldSale({ id: saleId });
      if (this.$route.query.recall) {
        this.$router.replace({ path: "/pos", query: {} });
      }
    },
    async recallHeldSale(sale) {
      this.showHeldPanel = false;
      this.processing = true;
      try {
        const full = await ApiService.get(`sales/${sale.id}`);
        this.activeSaleId = full.id;
        this.recalledSale = full;
        this.cartItems = full.items.map((item) => ({
          cartLineId: item.id,
          productId: item.productId,
          productName: item.productName,
          miscLabel: item.miscLabel,
          unitPrice: item.unitPrice,
          taxRate: 0,
          quantity: item.quantity,
          maxAvailable: 9999,
          readOnly: true,
        }));
        if (full.customer) {
          this.selectedCustomer = {
            id: full.customer.id,
            fullName: full.customer.fullName,
            loyaltyPoints: full.customer.loyaltyPoints || 0,
            discountPercent: 0,
          };
        }
        this.paymentLines = [];
        this.showPayment = true;
      } catch (e) {
        toast.fire({
          title: e?.response?.data?.message || "Could not recall sale",
          icon: "danger",
        });
      } finally {
        this.processing = false;
      }
    },
    async validatePromotion() {
      const code = this.promotionCode.trim();
      if (!code) return;
      this.validatingPromotion = true;
      this.promotionError = "";
      try {
        this.promotionInfo = await ApiService.post("promotions/validate", {
          code,
          storeId: this.selectedStoreId,
        });
        await this.refreshPreview();
      } catch (e) {
        this.promotionInfo = null;
        this.promotionError =
          e?.response?.data?.message || "Invalid promotion code";
      } finally {
        this.validatingPromotion = false;
      }
    },
    async initiateStk() {
      const phone = this.mobileMoneyPhone.trim();
      if (!phone) return;
      const amount = this.remainingDue || this.amountDue;
      this.stkLoading = true;
      try {
        const result = await ApiService.post("pos/mobile-money/stk", {
          storeId: this.selectedStoreId,
          saleId: this.activeSaleId || null,
          phone,
          amount,
        });
        toast.fire({
          title: result.message || "STK push initiated",
          icon: "success",
        });
        if (result.reference) {
          this.currentPayment.reference = result.reference;
        }
      } catch (e) {
        toast.fire({
          title: e?.response?.data?.message || "STK request failed",
          icon: "danger",
        });
      } finally {
        this.stkLoading = false;
      }
    },
    debouncedCustomerSearch() {
      clearTimeout(this.customerSearchTimer);
      this.customerSearchTimer = setTimeout(this.searchCustomers, 300);
    },
    async searchCustomers() {
      const q = this.customerSearch.trim();
      if (q.length < 2) {
        this.customerResults = [];
        return;
      }
      this.customerResults = await ApiService.get("customers", {
        query: q,
        limit: 8,
      });
    },
    selectCustomer(customer) {
      this.selectedCustomer = customer;
      this.customerSearch = "";
      this.customerResults = [];
      this.loyaltyPointsToRedeem = 0;
      if (!this.activeSaleId) this.refreshPreview();
    },
    clearCustomer() {
      this.selectedCustomer = null;
      this.loyaltyPointsToRedeem = 0;
      if (!this.activeSaleId) this.refreshPreview();
    },
    async createCustomer(bvModalEvt) {
      bvModalEvt.preventDefault();
      try {
        const customer = await ApiService.post("customers", this.quickCustomer);
        this.selectCustomer(customer);
        this.showQuickCustomer = false;
        this.quickCustomer = {
          firstName: "",
          lastName: "",
          phone: "",
          email: "",
        };
      } catch (e) {
        toast.fire({
          title: e?.response?.data?.message || "Could not create customer",
          icon: "danger",
        });
      }
    },
    async openMiscModal() {
      if (!this.miscProduct) {
        try {
          this.miscProduct = await ApiService.get("sales/misc-product");
        } catch (e) {
          toast.fire({
            title: "Misc product unavailable",
            icon: "danger",
          });
          return;
        }
      }
      this.miscForm = { label: "", unitPrice: 0 };
      this.showMiscModal = true;
    },
    addMiscItem() {
      const price = Number(this.miscForm.unitPrice);
      if (!price || price <= 0) return;
      this.cartItems.push({
        cartLineId: this.nextCartLineId("misc"),
        productId: this.miscProduct.id,
        productName: this.miscForm.label || this.miscProduct.name,
        miscLabel: this.miscForm.label || null,
        unitPrice: price,
        taxRate: 0,
        quantity: 1,
        maxAvailable: 9999,
      });
      this.showMiscModal = false;
      focusScanner(this);
    },
    isNetworkError(e) {
      return !e?.response && (!isOnline() || e?.message === "Network Error");
    },
    async flushOfflineIfOnline() {
      if (!isOnline()) return;
      try {
        const result = await flushOfflineQueue((path, payload) =>
          ApiService.post(path, payload),
        );
        if (result.synced > 0) {
          toast.fire({
            title: `${result.synced} offline sale(s) synced`,
            icon: "success",
          });
        }
      } catch (e) {
        /* ignore */
      }
    },
    async submitPayments(saleId, payments) {
      for (const p of payments) {
        await ApiService.post(`sales/${saleId}/payments`, {
          method: p.method,
          amount: p.amount,
          tenderedAmount:
            p.method === "Cash" ? Number(p.tendered || p.amount) : null,
          reference: p.reference || null,
        });
      }
    },
    async finishSale(saleId, saleNumber) {
      const receipt = await ApiService.get(`sales/${saleId}/receipt`);
      this.receiptHtml = receipt.htmlContent;
      this.receiptStatus = receipt.sale?.status || "completed";
      this.lastSaleId = saleId;
      this.showPayment = false;
      this.clearCart();
      this.showReceipt = true;
      await this.loadProducts();
      await this.loadHeldSales();
      focusScanner(this);
      printEscPos(saleId).catch(() => {});
      toast.fire({
        title: `Sale ${saleNumber || ""} completed`.trim(),
        icon: "success",
      });
    },
    async completeSale() {
      if (!this.shiftOpen) {
        toast.fire({
          title: "Open a cash drawer shift before completing sales.",
          icon: "warning",
        });
        return;
      }

      const payments = this.getPaymentsToSubmit();
      if (!payments.length) {
        toast.fire({ title: "Add at least one payment", icon: "warning" });
        return;
      }

      this.processing = true;
      let saleId = this.activeSaleId;

      try {
        if (this.activeSaleId) {
          await this.submitPayments(this.activeSaleId, payments);
          const updated = await ApiService.get(`sales/${this.activeSaleId}`);
          await this.finishSale(updated.id, updated.saleNumber);
          return;
        }

        const sale = await ApiService.post("sales", this.buildSalePayload());
        saleId = sale.id;
        await this.submitPayments(sale.id, payments);
        await this.finishSale(sale.id, sale.saleNumber);
      } catch (e) {
        if (saleId && !this.activeSaleId) {
          try {
            await ApiService.post(`sales/${saleId}/cancel`);
          } catch (cancelErr) {
            /* ignore */
          }
        }

        if (this.isNetworkError(e) && !this.activeSaleId) {
          enqueueOfflineSale(this.buildSalePayload());
          this.showPayment = false;
          this.clearCart();
          toast.fire({
            title: "Offline — sale queued for sync",
            icon: "warning",
          });
          return;
        }

        toast.fire({
          title: e?.response?.data?.message || "Checkout failed",
          icon: "danger",
        });
      } finally {
        this.processing = false;
      }
    },
    printReceipt() {
      const w = window.open("", "_blank");
      if (!w) return;
      w.document.write(this.receiptHtml);
      w.document.close();
      w.focus();
      w.print();
    },
    async printHeldReceipt(sale) {
      this.showHeldPanel = false;
      try {
        const receipt = await ApiService.get(`sales/${sale.id}/receipt`);
        this.receiptHtml = receipt.htmlContent;
        this.receiptStatus = receipt.sale?.status || "held";
        this.lastSaleId = sale.id;
        this.showReceipt = true;
      } catch (e) {
        toast.fire({
          title: e?.response?.data?.message || "Could not load receipt",
          icon: "danger",
        });
      }
    },
    async downloadEscPos() {
      if (!this.lastSaleId) return;
      try {
        const ok = await printEscPos(this.lastSaleId);
        if (!ok) {
          toast.fire({ title: "ESC/POS data unavailable", icon: "warning" });
        }
      } catch (e) {
        toast.fire({
          title: e?.response?.data?.message || "ESC/POS download failed",
          icon: "danger",
        });
      }
    },
  },
};
</script>

<style scoped>
.pos-page--tablet-friendly .pos-container {
  min-height: calc(100vh - 6rem);
}

.pos-page--tablet-friendly .product-grid {
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
}

.pos-shortcut-bar {
  display: inline-flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.35rem 0.75rem;
  padding: 0.3rem 0.55rem;
  margin-bottom: 0.65rem;
  border-radius: var(--pos-radius-sm, 6px);
  background: var(--pos-surface-muted, #f5f6f8);
  border: 1px solid var(--pos-border-subtle, #e2e8f0);
  font-size: 0.75rem;
  color: var(--pos-muted, #64748b);
}

.pos-shortcut-hint {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  white-space: nowrap;
}

.pos-shortcut-key {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 1.65rem;
  padding: 0.1rem 0.35rem;
  font-size: 0.68rem;
  font-family: inherit;
  font-weight: 600;
  line-height: 1.2;
  border: 1px solid #cbd5e1;
  border-radius: 4px;
  background: #fff;
  color: #334155;
  box-shadow: 0 1px 0 #e2e8f0;
}

.pos-search-row {
  display: flex;
  gap: 0.5rem;
  align-items: stretch;
}

.pos-search-row .form-control,
.pos-search-input {
  flex: 1;
  min-width: 0;
}

.pos-search-misc-btn {
  flex-shrink: 0;
  white-space: nowrap;
  align-self: stretch;
}

.pos-empty-catalog {
  padding: 1.5rem 0.25rem;
  color: #64748b;
  font-size: 0.875rem;
}

.pos-cart-actions {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.5rem;
  margin-top: 0.75rem;
  width: 100%;
}

.pos-cart-actions .pos-action-btn {
  width: 100%;
  min-width: 0;
  max-width: 100%;
  min-height: 42px;
  padding: 0.5rem 0.75rem;
  font-size: 1rem;
  font-weight: 600;
  line-height: 1.25;
  white-space: nowrap;
  box-shadow: none;
}

.pos-cart-actions .pos-action-btn.akkurate-green-btn {
  border: none;
}

.pos-held-btn {
  flex-shrink: 0;
  white-space: nowrap;
  min-height: 38px;
  box-shadow: none !important;
}

.pos-qty-btn {
  min-width: 38px;
  min-height: 38px;
  padding: 0.35rem 0.55rem !important;
}

.held-banner {
  padding: 0.5rem 0.75rem;
  border-radius: var(--pos-radius-sm, 6px);
  background: rgba(255, 160, 0, 0.12);
  border: 1px solid rgba(255, 160, 0, 0.35);
}

.tender-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.tender-chip {
  padding: 0.35rem 0.75rem;
  border: 1px solid var(--surface-border, #dee2e6);
  border-radius: 999px;
  background: var(--pos-surface, #fff);
  font-size: 0.85rem;
  cursor: pointer;
  transition: background 0.12s ease, border-color 0.12s ease;
}

.tender-chip:hover {
  background: var(--pos-surface-muted, #f5f6f8);
  border-color: var(--akkurate-green, #018673);
}

.split-payment-line {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.35rem 0;
  border-bottom: 1px dashed var(--surface-border, #dee2e6);
  font-size: 0.9rem;
}

.mobile-money-section {
  padding: 0.75rem;
  border-radius: var(--pos-radius-sm, 6px);
  background: var(--pos-surface-muted, #f5f6f8);
}

@media (max-width: 768px) {
  .pos-page--tablet-friendly .pos-cart-actions {
    grid-template-columns: 1fr;
  }

  .pos-shortcut-bar {
    display: none;
  }
}
</style>

<style>
.quick-key-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: 0.5rem;
}

.quick-key-btn {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 0.5rem 0.65rem;
  border: 1px solid var(--surface-border, #dee2e6);
  border-radius: var(--pos-radius-sm, 6px);
  background: var(--pos-surface, #fff);
  cursor: pointer;
  text-align: left;
  transition: border-color 0.12s ease, box-shadow 0.12s ease;
}

.quick-key-btn:hover:not(:disabled) {
  border-color: var(--akkurate-green, #018673);
  box-shadow: var(--pos-shadow-sm, 0 1px 3px rgba(0, 0, 0, 0.08));
}

.quick-key-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.quick-key-btn__name {
  font-size: 0.8rem;
  font-weight: 600;
  line-height: 1.2;
}

.quick-key-btn__price {
  font-size: 0.75rem;
  color: var(--akkurate-green, #018673);
  margin-top: 0.15rem;
}

.held-item {
  display: flex;
  align-items: stretch;
  width: 100%;
  padding: 0;
  margin-bottom: 0.5rem;
  border: 1px solid var(--surface-border, #dee2e6);
  border-radius: var(--pos-radius-sm, 6px);
  background: var(--pos-surface, #fff);
  overflow: hidden;
}

.held-item:hover {
  border-color: var(--akkurate-green, #018673);
}

/* Beat global .btn width/shadow so POS actions stay aligned */
.pos-page .pos-search-row .form-control,
.pos-page .pos-search-row .btn {
  min-height: 42px;
  padding: 0.5rem 0.75rem !important;
  font-size: 1rem !important;
  box-shadow: none !important;
  border-radius: 0.25rem !important;
}

.pos-page .pos-cart-actions {
  display: grid !important;
  grid-template-columns: repeat(3, minmax(0, 1fr)) !important;
  gap: 0.5rem;
  width: 100%;
}

.pos-page .pos-cart-actions .btn {
  width: 100% !important;
  min-width: 0 !important;
  max-width: 100% !important;
  min-height: 42px !important;
  margin: 0 !important;
  padding: 0.5rem 0.75rem !important;
  font-size: 1rem !important;
  line-height: 1.25 !important;
  box-shadow: none !important;
  border-radius: 0.25rem !important;
}

.pos-page .pos-held-btn,
.pos-page .pos-search-misc-btn {
  box-shadow: none !important;
}

.pos-page .pos-qty-btn {
  min-width: 38px;
  min-height: 38px;
  padding: 0.35rem 0.55rem !important;
  box-shadow: none !important;
}

.pos-page .cart-items {
  min-height: 0;
  overflow-y: auto;
}

@media (max-width: 768px) {
  .pos-page .pos-cart-actions {
    grid-template-columns: 1fr !important;
  }
}

.stock-filter {
  display: flex;
  align-items: center !important;
  gap: 12px;
}

.stock-filter .filter-label {
  font-size: 14px;
  font-weight: 600;
  color: #495057;
  white-space: nowrap;
}

.stock-radio-group {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

/* Remove Bootstrap's default appearance */
.stock-radio-group .btn {
  border: 1px solid #d9e2dd;
  background: #ffffff;
  color: #495057;
  border-radius: 6px !important;
  padding: 7px 13px;
  font-size: 13px;
  font-weight: 500;
  transition: all 0.15s ease;
  display: flex;
  align-items: center !important;
  gap: 5px;
}

/* Hover */
.stock-radio-group .btn:hover {
  background: #f1f8f4;
  border-color: #76b89a;
  color: #167a4a;
}

/* Selected */
.stock-radio-group .btn.active,
.stock-radio-group .btn:active {
  background: #198754;
  border-color: #198754;
  color: #ffffff;
  box-shadow: none;
}

/* Focus */
.stock-radio-group .btn:focus {
  box-shadow: 0 0 0 2px rgba(25, 135, 84, 0.15);
}
</style>
